import { type EventContentArg } from "@fullcalendar/core"
import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  calendarEventCardAddress,
  calendarEventCardLine
} from "../../helpers/calendar-event-card-title.ts"

const { Text } = Typography

interface CalendarEventCellEventProps {
  eventInfo: EventContentArg
}

export function CalendarEventCellEvent({
  eventInfo
}: CalendarEventCellEventProps) {
  const { t } = useTranslation()
  const event = eventInfo?.event
  const input = { title: event?.title, record: event?.extendedProps }
  const line = calendarEventCardLine(input, t)
  const address = calendarEventCardAddress(input)

  // The slot's placement already says when the event is, so the card spends
  // its two lines on what it is and where. A sub-30-minute slot only has
  // room for the first.
  const diffInMinutes = dayjs(event?.end).diff(dayjs(event?.start), "minute")
  const isShortEvent = diffInMinutes < 30

  // Struck through rather than hidden: whoever was invited needs to see that
  // the meeting was cancelled, not find it silently gone from the grid.
  const isCancelled = event?.extendedProps?.status === "CANCELLED"

  return (
    <Flex
      vertical
      className={`overflow-hidden size-full ${isCancelled ? "line-through opacity-60" : ""}`}
      data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
    >
      <Text ellipsis className="text-xs text-inherit font-semibold leading-4">
        {line}
      </Text>

      {address && !isShortEvent && (
        <Text ellipsis className="text-xs text-inherit opacity-80 leading-4">
          {address}
        </Text>
      )}
    </Flex>
  )
}
